import * as React from "react";
import {useContext, useMemo, useState} from "react";
import {GpsShapeType, GpsTrigger, LatLong, MarkerSpot, PolygonSpot, SpotType} from "../../components/types";
import {v4 as uuidv4} from 'uuid';
import {AppContext, AppContextI} from "../../components/context/ContextWrapper";
import {CircleMapShape, MapShape, MarkerMapShape, PolygonMapShape} from "../../components/map/MapTypes";
import {APIProvider} from "@vis.gl/react-google-maps";
import {AdvancedMap} from "../../components/map/AdvancedMap";
import {MapContext, MapContextI} from "../../components/context/MapContextWrapper";
import {Box, Button, Flex, Heading, IconButton, Text} from "@chakra-ui/react";
import {ChevronLeftIcon, ChevronRightIcon} from "@chakra-ui/icons";
import MapDrawerContent from "./MapDrawerContent";
import {UserContext, UserContextI} from "../../UserContext";
import {isRootAdminRole} from "../../auth/types";
import {useWalkLines} from "../../components/walkLines/useWalkLines";
import WalkLineToolbar from "../../components/walkLines/WalkLineToolbar";

type Props = {
    isSoundDropTarget?: boolean;
    onSoundDragOver?: (event: React.DragEvent<HTMLDivElement>) => void;
    onSoundDragLeave?: (event: React.DragEvent<HTMLDivElement>) => void;
    onSoundDrop?: (event: React.DragEvent<HTMLDivElement>, position?: LatLong) => void | Promise<void>;
};

const AreaDetails = ({
    isSoundDropTarget,
    onSoundDragOver,
    onSoundDragLeave,
    onSoundDrop
}: Props) => {
    const appContext: AppContextI = useContext<AppContextI>(AppContext);
    const mapContext: MapContextI = useContext<MapContextI>(MapContext);
    const userContext: UserContextI = useContext<UserContextI>(UserContext);

    const area = appContext.getArea();
    const spots = (appContext.getSpots() || []).filter((spot): spot is MarkerSpot | PolygonSpot => !!spot && !!spot.type);
    const displayedVersion = appContext.getDisplayedVersion();
    const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
    const isRootAdmin = isRootAdminRole(userContext.getUser()?.role);
    const canEdit = appContext.isDraft();
    const customMapShapes = mapContext.getMapShapes();

    const {
        resolvedWalkLines,
        editableWalkLines,
        walkLineEditCallbacks,
        walkLineStartSpotIds,
        activeLines,
        handleGenerateWalkLines,
        handleClearWalkLines,
    } = useWalkLines(area?.id, spots, canEdit);

    const hasCircleTrigger = (spot: MarkerSpot | PolygonSpot) =>
        spot.gpsTriggers?.some((t: GpsTrigger) => t.shapeType === GpsShapeType.Circle);

    const markerShapes: MarkerMapShape[] = useMemo(() => spots.filter(value => value.type === SpotType.Marker && !hasCircleTrigger(value))
        ?.map((markerSpot: MarkerSpot) => {
            const markerMapShape = {
                id: markerSpot.id,
                name: markerSpot.name,
                hoverLabel: `Spot: ${markerSpot.name || "Unnamed spot"}`,
                position: markerSpot.position,
                trigger: {
                    id: markerSpot.id,
                    type: "GPS",
                    shapeType: GpsShapeType.Marker
                }
            } as MarkerMapShape;

            if (canEdit) {
                markerMapShape["setPositionCallback"] = (center: google.maps.LatLngLiteral) => {
                    const newPos = {
                        lat: center.lat,
                        lng: center.lng
                    };
                    if (newPos.lat !== markerSpot.position.lat && newPos.lng !== markerSpot.position.lng) {
                        const oldSpot = {...markerSpot};
                        markerSpot.position = newPos;
                        appContext.updateSpot(oldSpot, {
                            ...oldSpot,
                            position: newPos,
                        } as any)
                    }
                }
            }
            return markerMapShape
        }), [canEdit, spots, appContext]);

    const polygonShapes: PolygonMapShape[] = useMemo(() => spots.filter(value => value.type === SpotType.Polygon)
        .map((polygonSpot: PolygonSpot) => {
            const gpsTrigger = polygonSpot.gpsTriggers?.[0];
            const coords = gpsTrigger?.polygonCoords || polygonSpot.coords || [];
            return {
                id: polygonSpot.id,
                hoverLabel: `Spot: ${polygonSpot.name || "Unnamed spot"}`,
                trigger: {
                    id: gpsTrigger?.id || polygonSpot.id,
                    type: "GPS",
                    shapeType: GpsShapeType.Polygon,
                    polygonCoords: coords
                },
                onClick: () => {
                    mapContext.setTriggerSelection(undefined);
                    mapContext.setSelectedShapeType(undefined);
                    appContext.setSelection(polygonSpot);
                },
                setCoordsCallback: canEdit ? (coords: google.maps.LatLngLiteral[]) => {
                    const updatedTriggers = polygonSpot.gpsTriggers?.[0]
                        ? [{ ...polygonSpot.gpsTriggers[0], polygonCoords: coords }]
                        : [{
                            id: uuidv4(),
                            type: "GPS",
                            shapeType: GpsShapeType.Polygon,
                            polygonCoords: coords
                        }];
                    appContext.updateSpot(polygonSpot, {
                        ...polygonSpot,
                        coords: coords,
                        gpsTriggers: updatedTriggers
                    } as PolygonSpot);
                } : undefined
            } as PolygonMapShape
        }), [canEdit, spots, appContext, mapContext]);

    const circleShapes: CircleMapShape[] = useMemo(() => spots
        .filter((value): value is MarkerSpot => value.type === SpotType.Marker && !!hasCircleTrigger(value))
        .map((markerSpot: MarkerSpot) => {
            const gpsTrigger = markerSpot.gpsTriggers?.find((t: GpsTrigger) => t.shapeType === GpsShapeType.Circle);
            return {
                id: markerSpot.id,
                hoverLabel: `Spot: ${markerSpot.name || "Unnamed spot"}`,
                center: markerSpot.position,
                radius: gpsTrigger?.circleRadius || 50,
                trigger: {
                    id: gpsTrigger?.id || markerSpot.id,
                    type: "GPS",
                    shapeType: GpsShapeType.Circle,
                    circleRadius: gpsTrigger?.circleRadius || 50
                },
                onClick: () => {
                    mapContext.setTriggerSelection(undefined);
                    mapContext.setSelectedShapeType(undefined);
                    appContext.setSelection(markerSpot);
                },
                setCenterCallback: canEdit ? (center: google.maps.LatLngLiteral) => {
                    appContext.updateSpot(markerSpot, {
                        ...markerSpot,
                        position: {lat: center.lat, lng: center.lng}
                    } as MarkerSpot);
                } : undefined,
                setRadiusCallback: canEdit ? (radius: number) => {
                    const updatedTriggers = (markerSpot.gpsTriggers || []).map((t: GpsTrigger) =>
                        t.shapeType === GpsShapeType.Circle ? {...t, circleRadius: Math.round(radius)} : t
                    );
                    appContext.updateSpot(markerSpot, {
                        ...markerSpot,
                        gpsTriggers: updatedTriggers
                    } as MarkerSpot);
                } : undefined
            } as CircleMapShape;
        }), [canEdit, spots, appContext, mapContext]);

    const mapShapes: MapShape[] = useMemo(() => [
        ...customMapShapes,
        ...markerShapes,
        ...polygonShapes,
        ...circleShapes
    ], [customMapShapes, markerShapes, polygonShapes, circleShapes]);

    const toggleDrawer = () => setIsDrawerOpen((prev) => !prev);
    const versionLabel = displayedVersion ? (displayedVersion.publishedAt === 0 ? "Draft" : "Published") : undefined;

    return !area ? <div>Loading...</div> :
        <APIProvider apiKey={"AIzaSyCcdolz5IqPuQZ5MuZdXEHVgdoTukvbblE"}>
            <Box position={"relative"} height={"100%"} minHeight={0} overflow={"hidden"} bg={"#f8fafc"}>
                <Box height={"100%"} minHeight={0}>
                    <AdvancedMap
                        mapShapes={mapShapes}
                        walkLines={resolvedWalkLines}
                        editableWalkLines={editableWalkLines}
                        walkLineEditCallbacks={canEdit ? walkLineEditCallbacks : undefined}
                        walkLineStartSpotIds={walkLineStartSpotIds}
                        isSoundDropTarget={!!isSoundDropTarget}
                        onSoundDragOver={onSoundDragOver}
                        onSoundDragLeave={onSoundDragLeave}
                        onSoundDrop={onSoundDrop}
                    />
                </Box>

                {canEdit && isRootAdmin && (
                    <WalkLineToolbar
                        hasLines={activeLines.length > 0}
                        onGenerate={handleGenerateWalkLines}
                        onClear={handleClearWalkLines}
                    />
                )}

                {isRootAdmin && (
                    <>
                        <Box
                            position={"absolute"}
                            top={0}
                            right={0}
                            height={"100%"}
                            zIndex={4}
                            pointerEvents={"none"}
                        >
                            <Box
                                position={"absolute"}
                                top={0}
                                right={0}
                                height={"100%"}
                                width={"1px"}
                                bg={"#cbd5e0"}
                                pointerEvents={"none"}
                            />
                        </Box>
                        <IconButton
                            aria-label={isDrawerOpen ? "Hide map drawer" : "Show map drawer"}
                            icon={isDrawerOpen ? <ChevronRightIcon/> : <ChevronLeftIcon/>}
                            onClick={toggleDrawer}
                            size={"md"}
                            borderRadius={"md"}
                            boxShadow={"xl"}
                            bg={"white"}
                            color={"gray.700"}
                            border={"1px solid"}
                            borderColor={"#cbd5e0"}
                            _hover={{
                                bg: "gray.50",
                                color: "blue.600",
                                boxShadow: "2xl",
                                borderColor: "#a0aec0"
                            }}
                            title={isDrawerOpen ? "Hide drawer" : "Show drawer"}
                            position={"absolute"}
                            top={"50%"}
                            right={0}
                            transform={"translateY(-50%)"}
                            pointerEvents={"auto"}
                            zIndex={4}
                        />

                        <Box
                            role={"complementary"}
                            aria-hidden={!isDrawerOpen}
                            position={"absolute"}
                            inset={0}
                            bg={"white"}
                            boxShadow={"-10px 0 30px rgba(0, 0, 0, 0.18)"}
                            transform={isDrawerOpen ? "translateX(0)" : "translateX(100%)"}
                            opacity={isDrawerOpen ? 1 : 0}
                            transition={"transform 0.35s ease, opacity 0.35s ease"}
                            zIndex={2}
                            display={"flex"}
                            flexDirection={"column"}
                            pointerEvents={isDrawerOpen ? "auto" : "none"}
                            borderLeft={"1px solid #e2e8f0"}
                            minHeight={0}
                        >
                            <Flex
                                alignItems={"center"}
                                justifyContent={"space-between"}
                                padding={"16px"}
                                borderBottom={"1px solid #e2e8f0"}
                                gap={3}
                            >
                                <Box>
                                    <Text fontSize={"xs"} color={"gray.500"} textTransform={"uppercase"} letterSpacing={"0.08em"}>
                                        Flow overview
                                    </Text>
                                    <Heading size={"sm"} color={"gray.800"} lineHeight={1.3}>
                                        {area?.name || "Space overview"}
                                    </Heading>
                                </Box>
                                <Button
                                    onClick={toggleDrawer}
                                    size={"sm"}
                                    variant={"ghost"}
                                    rightIcon={<ChevronRightIcon/>}
                                >
                                    Hide
                                </Button>
                            </Flex>

                            {isDrawerOpen && (
                                <MapDrawerContent
                                    areaName={area?.name}
                                    spots={spots}
                                    versionLabel={versionLabel}
                                    flowView={"custom"}
                                />
                            )}
                        </Box>
                    </>
                )}
            </Box>
        </APIProvider>
}
export default AreaDetails;
